import {
	AxisCode,
	type FieldValue,
	HookCode,
	type IFieldChange,
	LegalApplicability,
	RequiredWorkOrderField,
	WorkType,
} from '@atlas/types'

/** A value as a work order or a canonical record holds it, before it is compared. */
export type RawFieldValue = unknown

const isMoney = (value: object): value is { amountMinor: number; currency: string } =>
	'amountMinor' in value &&
	typeof value.amountMinor === 'number' &&
	'currency' in value &&
	typeof value.currency === 'string'

/**
 * The comparable form of a value: a date as its ISO string, an empty string or
 * `undefined` and an empty list as null, money as `{ amountMinor, currency }`, a list of strings as a
 * copy, and any other structure as its JSON text. Whether a list's order matters is
 * the caller's choice: see {@link sortedCodes}.
 */
export function normalizeFieldValue(value: RawFieldValue): FieldValue {
	if (value === undefined || value === null || value === '') return null
	if (value instanceof Date) return value.toISOString()
	if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value
	if (Array.isArray(value) && value.length === 0) return null
	if (Array.isArray(value) && value.every((item) => typeof item === 'string')) return [...value]
	if (typeof value === 'object' && !Array.isArray(value) && isMoney(value)) {
		return { amountMinor: value.amountMinor, currency: value.currency }
	}
	return JSON.stringify(value)
}

/** Codes compared as a set: sorted and de-duplicated, as a tuple axis is. */
export function sortedCodes(codes: readonly string[] | null | undefined): string[] {
	return [...new Set(codes ?? [])].sort()
}

function sameValue(left: FieldValue, right: FieldValue): boolean {
	if (Array.isArray(left) || Array.isArray(right)) {
		return (
			Array.isArray(left) &&
			Array.isArray(right) &&
			left.length === right.length &&
			left.every((v, i) => v === right[i])
		)
	}
	if (typeof left === 'object' && left !== null && typeof right === 'object' && right !== null) {
		return left.amountMinor === right.amountMinor && left.currency === right.currency
	}
	return left === right
}

/**
 * The fields whose value differs between `before` and `after`, in the order of
 * `fields`, each with its normalised value on both sides. A field missing from
 * `after` is not compared: the caller lists only what it means to set.
 */
export function diffFields(before: object, after: object, fields: readonly string[]): IFieldChange[] {
	const changes: IFieldChange[] = []
	for (const field of fields) {
		if (!(field in after)) continue
		const was = normalizeFieldValue(Reflect.get(before, field))
		const now = normalizeFieldValue(Reflect.get(after, field))
		if (!sameValue(was, now)) changes.push({ field, before: was, after: now })
	}
	return changes
}

/**
 * What a `fields_present` guard reads off an order: its descriptors, its due date and its
 * classification tuple, and, read only when a guard asks for them, whether it has a main copy
 * and its campaign's materials. A fact left undefined counts as missing, so a reader that
 * forgets one refuses the move rather than letting it through.
 */
export interface RequiredFieldFacts {
	assetTypeCode: string | null
	complexityLevel: string | null
	dueAt: string | null
	tuple: Readonly<Partial<Record<string, readonly string[]>>> | null | undefined
	hasMainCopy?: boolean
	hasCampaignMaterials?: boolean
}

const hasAxis = (facts: RequiredFieldFacts, axis: AxisCode): boolean => (facts.tuple?.[axis]?.length ?? 0) > 0

const IS_PRESENT: Readonly<Record<RequiredWorkOrderField, (facts: RequiredFieldFacts) => boolean>> = {
	[RequiredWorkOrderField.ASSET_TYPE]: (facts) => !!facts.assetTypeCode,
	[RequiredWorkOrderField.COMPLEXITY]: (facts) => !!facts.complexityLevel,
	// A product is a child on the brand axis, so it counts as a brand too.
	[RequiredWorkOrderField.BRAND]: (facts) => hasAxis(facts, AxisCode.BRAND),
	[RequiredWorkOrderField.MARKET]: (facts) => hasAxis(facts, AxisCode.GEOGRAPHY),
	[RequiredWorkOrderField.DUE_DATE]: (facts) => !!facts.dueAt,
	[RequiredWorkOrderField.PLATFORM]: (facts) => hasAxis(facts, AxisCode.CHANNEL),
	[RequiredWorkOrderField.MAIN_COPY]: (facts) => facts.hasMainCopy === true,
	[RequiredWorkOrderField.CAMPAIGN_MATERIALS]: (facts) => facts.hasCampaignMaterials === true,
}

/** The listed fields the order lacks, in the order they were listed. */
export const missingRequiredFields = (
	facts: RequiredFieldFacts,
	fields: readonly RequiredWorkOrderField[],
): RequiredWorkOrderField[] => fields.filter((field) => !IS_PRESENT[field](facts))

const isRequiredField = (value: unknown): value is RequiredWorkOrderField =>
	Object.values<unknown>(RequiredWorkOrderField).includes(value)

/** The fields a `fields_present` hook's params list, ignoring anything that is not one. */
export const requiredFieldsOf = (params: { fields?: unknown }): RequiredWorkOrderField[] =>
	Array.isArray(params.fields) ? params.fields.filter(isRequiredField) : []

/** Whether the content is something other than spaces. */
export const isFilledText = (content: string | null | undefined): boolean => !!content && content.trim().length > 0

/**
 * Whether a legal text applies to a piece, as the distribution package states it: `NA`
 * (any case, spaces ignored) means it does not, any other text that it does, and no text
 * that nobody said.
 */
export function legalApplicability(text: string | null | undefined): LegalApplicability {
	if (!isFilledText(text)) return LegalApplicability.UNKNOWN
	return text?.trim().toUpperCase() === 'NA' ? LegalApplicability.NOT_APPLICABLE : LegalApplicability.APPLICABLE
}

/** The kind of work an asset type is, by its family letter: `P` print, `V` video, the rest digital. */
export function workTypeOf(assetTypeCode: string | null | undefined): WorkType | null {
	if (!assetTypeCode) return null
	if (assetTypeCode.startsWith('P')) return WorkType.PRINT
	if (assetTypeCode.startsWith('V')) return WorkType.VIDEO
	return WorkType.DIGITAL
}

/** The fields the `fields_present` hooks among `hooks` require, each once. */
export const requiredFieldsOfHooks = (
	hooks: readonly { hookCode: string; params?: { fields?: unknown } | null }[],
): RequiredWorkOrderField[] => [
	...new Set(
		hooks.filter((h) => h.hookCode === HookCode.FIELDS_PRESENT).flatMap((h) => requiredFieldsOf(h.params ?? {})),
	),
]
export * from './design-files.js'
